import { JSX, useMemo } from "react";
import { QESButton } from "./Button";

export interface WizardStepsProps {
  /**
   * This is 0-indexed
   */
  currentStep: number;
  /**
   * A function to send the index/page number back to the wizard.
   * This way, the steps don't need to know anything about the wizard, its context, or the pages enum/object itself
   */
  setStep: (index: number) => void;
  /**
   *  The names of each steps. TS can't constrain a generic to Enum, so this'll do
   */
  steps: string[];
}

const emptyCircleSvg = (
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="wizard-steps__icon" aria-hidden="true">
    <circle cx="12" cy="12" r="11" strokeWidth="1" fill="none" stroke="#056edf" />
  </svg>
);

const nestedFilledCircleSvg = (
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="wizard-steps__icon" aria-hidden="true">
    {/* to precisely fill the whole viewbox, r needs to be half the width minus the strokeWidth */}
    <circle cx="12" cy="12" r="11" strokeWidth="1" fill="none" stroke="#056edf" />
    <circle cx="12" cy="12" r="6" fill="#056edf" />
  </svg>
);

const checkmarkCircleSvg = (
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="wizard-steps__icon" aria-hidden="true">
    <circle cx="12" cy="12" r="11" strokeWidth="1" fill="#056edf" stroke="#056edf" />
    <path
      d='M10.37507 13.40516 
      L 8.29309 11.32318 
      L 8.11631 11.14641 
      L 7.93954 11.32318 
      L 7.173077 12.08964 
      L 6.9963 12.26642 
      L 7.173077 12.4432 
      L 10.19829 15.46841 
      L 10.37507 15.64518 
      L 10.55184 15.46841 
      L 17.0518 8.9684 
      L 17.2286 8.79163 
      L 17.0518 8.61485 
      L 16.28539 7.848393 
      L 16.10861 7.671616 
      L 15.93183 7.848393 
      L 10.37507 13.40516Z'
      fill="white" stroke="white" strokeWidth="1.5"
    />
  </svg>
);


/**
 * A generator for the steps to be followed in a wizard, e.g. bulk terminations or tagging
 */
export const WizardSteps = (props: WizardStepsProps): JSX.Element => {
  const renderSteps = useMemo(() => {
    return props.steps.map((step, index) => {

      if (props.currentStep > index) {
        // before current
        return (
          <QESButton onClick={() => props.setStep(index)} key={step} qesType="secondary" className="wizard-steps__step past text-dark" qesTestId={`wizard-steps__step-${index}`}>
            {checkmarkCircleSvg}
            <span className="wizard-steps__label">{step}</span>
          </QESButton>
        );
      } else if (props.currentStep === index) {
        // is current
        return (
          <span key={step} className="wizard-steps__step current">
            {nestedFilledCircleSvg}
            <span className="wizard-steps__label">{step}</span>
          </span>
        );
      } else {
        // after current
        return (
          <span key={step} className="wizard-steps__step">
            {emptyCircleSvg}
            <span className="wizard-steps__label">{step}</span>
          </span>
        );
      }
    });
  }, [props.steps, props.currentStep]);

  return <div className="wizard-steps">{renderSteps}</div>;
};
